import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { message, Modal } from "antd";
import useOptions from "features/masterData/hooks/useOptions";
import { deleteKeysByList, generateRandomString } from "utils/helper";
import { PAGE_TYPE } from "../schemas/incomeDeductionSchema";
import * as services from "../service/incomeDeductionApi";
import {
  setCurrentIncomeDeduction,
  setIsLoading,
} from "../slices/payrollSlice";
import { useTranslation } from "react-i18next";
import { getFundList } from "features/fund/services/fundApi";


function toInitialValues(initialValues = {}) {
  const {
    taxCalculation = {},
    frequencyOfPayment = {},
    incomeType = {},
    rounding = {},
    overtimeCode = {},
    ...rest
  } = initialValues;
  const modifiedValues = {
    ...rest,
    taxCalculation: taxCalculation.id,
    rounding: rounding.id,
    incomeType: incomeType.id,
    frequencyOfPayment: frequencyOfPayment.id,
    calculateByActualWorkdays: initialValues.calculateByActualWorkdays === "Y",
    calculateOnce: initialValues.calculateOnce === "Y",
    socialSecurityFund: initialValues.socialSecurityFund === "Y",
    providentFund: initialValues.providentFund === "Y",
    overTime: initialValues.overTime === "Y",
    lateOrAbsentDeduction: initialValues.lateOrAbsentDeduction === "Y",
    overtimeCode: overtimeCode,
    offPeriod: initialValues?.offPeriod === "Y",
    previousPeriod: initialValues?.previousPeriod === "Y",
  };
  return modifiedValues;
}

function toParams(params = {}) {
  let modifiedParams = {
    ...params,
    incomeDeductionId: params.incomeDeducationId,
    incomeDeductinType: params.incomeType,
    calculateOnce: params.calculateOnce ? "Y" : "N",
    calculateByActualWorkdays: params.calculateByActualWorkdays ? "Y" : "N",
    lateOrAbsentDeduction: params.lateOrAbsentDeduction ? "Y" : "N",
    overTime: params.overTime ? "Y" : "N",
    providentFund: params.providentFund ? "Y" : "N",
    socialSecurityFund: params.socialSecurityFund ? "Y" : "N",
    type: PAGE_TYPE.INCOME,
    offPeriod: params.offPeriod ? "Y" : "N",
    previousPeriod: params.previousPeriod ? "Y" : "N",
  };
  // return deleteKeysByList(modifiedParams, ["offPeriod", "previousPeriod"]);
  return modifiedParams;
}

function useIncome() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const isLoading = useSelector((state) => state.payroll.isLoading);
  const currentIncomeDeduction = useSelector(
    (state) => state.payroll.currentIncomeDeduction
  );
  const {
    incomeDeductionByTypeOptions = [],
    roundingTypeOptions = [],
    frequencyOfPaymentOptions = [],
    taxCalculationOptions = [],
    getIncomeDeductionByTypeOptions,
    getRoundingTypeOptions,
    getFrequencyOfPaymentOptions,
    getTaxCalculationOptions,
  } = useOptions();

  const [fundOptions, setFundOptions] = useState([])
  const fetchFundOptions = useCallback(async () => {
    try {
      const response = await getFundList({
        requestId: generateRandomString(),
        companyId: currentCompany,
        page: 0,
        size: 200,
      });
      const list = response?.data?.content || [];
      console.log("list", list);

      setFundOptions(
        list.map((f) => ({
          value: f.id,
          label: i18n.language === "en" ? f.fundNameEn : f.fundNameTh,
          fundCode: f.fundCode,
          securitiesCompany: f.securitiesCompany,
          ...f
        }))
      );
    } catch (error) {
      setFundOptions([]);
    }
  }, [currentCompany, i18n.language]);

  useEffect(() => {
    fetchFundOptions();
  }, [fetchFundOptions]);

  const initialValues = useMemo(() => {
    return toInitialValues(currentIncomeDeduction);
  }, [currentIncomeDeduction]);

  const getIncome = useCallback(
    async (incomeId) => {
      try {
        dispatch(setIsLoading(true));
        if (incomeId) {
          const values = {
            incomeDeductionId: incomeId,
            requestId: generateRandomString(),
            companyId: currentCompany,
            type: PAGE_TYPE.INCOME,
          };
          const response = await services.getIncomeDeduction(values);
          dispatch(setCurrentIncomeDeduction(response.data));
        }
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const createIncome = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        const result = await services.createIncomeDeduction(toParams(values));
        message.success(t("common.success"))
        navigate("/configuration/payroll", { state: { selectedTab: "income" } });

        dispatch(setCurrentIncomeDeduction({}));
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, navigate]
  );

  const updateIncome = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        await services.updateIncomeDeduction(toParams(values));
        message.success(t("common.success"))
        navigate("/configuration/payroll", { state: { selectedTab: "income" } });
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, navigate]
  );

  const onSubmit = useCallback(
    (values) => {
      const modifiedValues = {
        ...values,
        incomeDeductionId: values.incomeDeducationId,
        companyId: currentCompany,
      };
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          <>
            <p>
              {t("dialog.confirmation.are_you_sure")}{" "}
              {values.incomeDeducationId ? t("action.edit") : t("action.create")} {t("employee.label.income")} {i18n.language === "th" ? "หรือไม่" : ""} ?
            </p>
          </>
        ),
        onOk() {
          if (values.incomeDeducationId) {
            updateIncome(modifiedValues);
          } else {
            createIncome(modifiedValues);
          }
        },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    },
    [currentCompany, updateIncome, createIncome]
  );

  const onCancel = useCallback(() => {
    navigate("/configuration/payroll", { state: { selectedTab: "income" } });
  }, [navigate]);

  useEffect(() => {
    if (typeof currentCompany !== "object" && params.id) {
      getIncome(params.id);
    }
  }, [currentCompany, params.id, getIncome]);

  useEffect(() => {
    if (typeof currentCompany !== "object") {
      getIncomeDeductionByTypeOptions({
        requestId: generateRandomString(),
        companyId: currentCompany,
        type: PAGE_TYPE.INCOME,
      });
    }
  }, [currentCompany, getIncomeDeductionByTypeOptions]);

  useEffect(() => {
    getRoundingTypeOptions();
  }, [getRoundingTypeOptions]);

  useEffect(() => {
    getFrequencyOfPaymentOptions();
  }, [getFrequencyOfPaymentOptions]);

  useEffect(() => {
    getTaxCalculationOptions();
  }, [getTaxCalculationOptions]);

  return {
    isLoading,
    initialValues,
    roundingTypeOptions,
    incomeDeductionByTypeOptions,
    frequencyOfPaymentOptions,
    taxCalculationOptions,
    fundOptions,
    onSubmit,
    onCancel,
  };
}

export default useIncome;
